import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Bot } from "@/lib/api";
import { Button, ErrorNote, Field, Loading, Select } from "@/components/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Copy settings from another bot (decision 55) — mostly to set up a new bot.
 * Super admin only; the server refuses anyone else. Every check runs before
 * anything is written, so a refusal leaves this bot exactly as it was.
 */

type Part = "sources" | "autoReplies" | "commands" | "footer" | "languages";

interface Summary {
  sources: number;
  autoReplies: number;
  commands: number;
  footerButtons: number;
  languages: { default: string; extra: string[] };
}

interface CopyResult {
  sources: { added: number; kept: number };
  autoReplies: { added: number };
  commands: { added: number; kept: number };
  footer: { buttons: number } | null;
  languages: { default: string; extra: string[] } | null;
}

const PARTS: { key: Part; label: string; count: (s: Summary) => string; effect: string }[] = [
  {
    key: "sources",
    label: "Sources",
    count: (s) => plural(s.sources, "source"),
    effect: "Adds the ones this bot does not have. Official is never copied.",
  },
  {
    key: "autoReplies",
    label: "Auto reply",
    count: (s) => plural(s.autoReplies, "rule"),
    effect: "Adds them after this bot's rules, above any catch-all.",
  },
  {
    key: "commands",
    label: "Commands",
    count: (s) => plural(s.commands, "command"),
    effect: "Adds the ones this bot does not have, and updates Telegram's menu.",
  },
  {
    key: "footer",
    label: "Bottom menu",
    count: (s) => plural(s.footerButtons, "button"),
    effect: "Replaces this bot's bottom menu.",
  },
  {
    key: "languages",
    label: "Languages",
    count: (s) => [s.languages.default, ...s.languages.extra].join(", "),
    effect: "Replaces this bot's languages.",
  },
];

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export function CopySettingsDialog({
  target,
  bots,
  onClose,
}: {
  target: Bot;
  bots: Bot[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const others = bots.filter((b) => b.id !== target.id);
  const [fromId, setFromId] = useState<number>(others[0]?.id ?? 0);
  const [parts, setParts] = useState<Set<Part>>(new Set());

  const summary = useQuery({
    queryKey: ["settings-summary", fromId],
    queryFn: () => api.get<Summary>(`/bots/${fromId}/settings-summary`),
    enabled: fromId > 0,
  });

  const copy = useMutation({
    mutationFn: () =>
      api.post<CopyResult>(`/bots/${target.id}/copy-settings`, {
        fromBotId: fromId,
        parts: [...parts],
      }),
    // Every page of this bot may now read differently.
    onSuccess: () => void queryClient.invalidateQueries(),
  });

  const toggle = (part: Part, on: boolean) => {
    const next = new Set(parts);
    if (on) next.add(part);
    else next.delete(part);
    setParts(next);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="break-all">Copy settings to @{target.username}</DialogTitle>
          <DialogDescription>
            Nothing is copied if any part would not fit — you are told what to tick or fix.
          </DialogDescription>
        </DialogHeader>

        {copy.data ? (
          <div className="grid gap-2">
            <p className="font-medium">Copied.</p>
            <ul className="grid list-disc gap-1 pl-5 text-muted-foreground">
              {parts.has("sources") && (
                <li>
                  Sources: {copy.data.sources.added} added, {copy.data.sources.kept} already here
                </li>
              )}
              {parts.has("autoReplies") && <li>Auto reply: {copy.data.autoReplies.added} added</li>}
              {parts.has("commands") && (
                <li>
                  Commands: {copy.data.commands.added} added, {copy.data.commands.kept} already here
                </li>
              )}
              {parts.has("footer") && (
                <li>Bottom menu: {plural(copy.data.footer?.buttons ?? 0, "button")}</li>
              )}
              {copy.data.languages && (
                <li>
                  Languages:{" "}
                  {[copy.data.languages.default, ...copy.data.languages.extra].join(", ")}
                </li>
              )}
            </ul>
            <DialogFooter>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="grid gap-4">
            <Field label="Copy from">
              <Select
                className="w-full"
                value={fromId}
                onChange={(e) => setFromId(Number(e.target.value))}
              >
                {others.map((b) => (
                  <option key={b.id} value={b.id}>
                    @{b.username}
                  </option>
                ))}
              </Select>
            </Field>

            {summary.isLoading ? (
              <Loading />
            ) : summary.error ? (
              <ErrorNote error={summary.error} />
            ) : (
              summary.data && (
                <div className="grid gap-2.5">
                  {PARTS.map((p) => (
                    <label key={p.key} className="flex items-start gap-2.5">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={parts.has(p.key)}
                        onChange={(e) => toggle(p.key, e.target.checked)}
                      />
                      <span className="min-w-0">
                        <span className="font-medium">{p.label}</span>{" "}
                        <span className="text-muted-foreground">· {p.count(summary.data)}</span>
                        <span className="block text-xs text-muted-foreground">{p.effect}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )
            )}

            {copy.error && <ErrorNote error={copy.error} />}

            <DialogFooter>
              <Button variant="quiet" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={parts.size === 0 || copy.isPending} onClick={() => copy.mutate()}>
                {copy.isPending ? "Copying…" : "Copy"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
